//'BY PERIOD' breakdown line graph.
import { ResponsiveLine, Serie } from '@nivo/line';
import { BumpSerie, ResponsiveAreaBump } from '@nivo/bump';
import React from 'react';
import Chip from './Chip';
import { AxisProps } from '@nivo/axes';
import { IAnalyticsColumn, IAnalyticsDimension, IAnalyticsReportOption } from './SalesAnalyticsTypes';
import { OrdinalColorScaleConfig } from '@nivo/colors';
import { LegendProps } from '@nivo/legends';
import { AnalysisGraphType, DashboardWidgetReportType } from '../../../graphql/schema/p4types';

const AXIS_BOTTOM: AxisProps = {
  tickSize: 5,
  tickPadding: 5,
  tickRotation: 0,
  legend: 'Period',
  legendOffset: 36,
  legendPosition: 'middle',
};

type ISalesAnalyticsLineProps = {
  formState: {
    dimension: IAnalyticsDimension | null | undefined;
    columns: Array<IAnalyticsColumn>;
    previousColumns: Array<IAnalyticsColumn>;
    data: Array<any>;
    secondDataSet: Array<any>;
    graphType: AnalysisGraphType;
    reportType: IAnalyticsReportOption | undefined;
  };
  height: number | `${string}px`;
  graphOnly: boolean;
  defaultLabel?: string;
  colorScheme?: OrdinalColorScaleConfig;
};

export const SalesAnalyticsLine = ({
  formState: {
    columns,
    previousColumns,
    dimension,
    data: currentData,
    secondDataSet: previousData,
    graphType,
    reportType,
  },
  height,
  graphOnly,
  defaultLabel = 'No Description',
  colorScheme: colors = { scheme: 'set2' },
}: ISalesAnalyticsLineProps) => {
  const marginConfig = { top: 10, right: 200, bottom: 30, left: 60 };
  const axisBottomConfig = { ...AXIS_BOTTOM };
  if (graphOnly) {
    marginConfig.right = 20;
    axisBottomConfig.legend = null;
  }

  let compareYears = false;
  let reformedData: Serie[] = [];
  if (reportType?.value === DashboardWidgetReportType.Monthly) {
    columns.sort((left, right) => (left?.periodNumber ?? 0) - (right?.periodNumber ?? 0));

    if (currentData.length === 1) {
      compareYears = true;
    }

    reformedData = currentData.map((data) => {
      let columnName = data.description;
      if (dimension && (dimension.value === 'CUSTOMER' || dimension.value === 'PRODUCT')) {
        //Use reference over description as it should be unique, description may not be (E.g. 'Cafe Zetta' for 'CAF001' and 'CAF002').
        columnName = data.reference || data.description;
      }
      if (!columnName) {
        columnName = defaultLabel;
      }
      return {
        id: columnName,
        data: columns
          .filter((column) => column.key.includes('period_'))
          .map((column) => {
            return {
              x: column.shortName,
              y: parseFloat(data[column.key]),
            };
          }),
      };
    });
  } else if (reportType?.value === DashboardWidgetReportType.Yearly) {
    reformedData = currentData.map((data) => {
      let columnName = data.description;
      if (dimension && (dimension.value === 'CUSTOMER' || dimension.value === 'PRODUCT')) {
        //Use reference over description as it should be unique, description may not be (E.g. 'Cafe Zetta' for 'CAF001' and 'CAF002').
        columnName = data.reference || data.description;
      }
      if (!columnName) {
        columnName = defaultLabel;
      }
      return {
        id: columnName,
        data: columns
          .filter((column) => column.key.includes('yearValueList'))
          .map((column) => {
            return {
              x: column.shortName,
              y: column?.index && column.index >= 0 ? parseFloat(data.yearValueList?.[column.index].value) : undefined,
            };
          }),
      };
    });
  }

  if (compareYears && previousData) {
    reformedData.push(
      ...previousData.map((data) => {
        let columnName = data.description;
        if (dimension && (dimension.value === 'CUSTOMER' || dimension.value === 'PRODUCT')) {
          //Use reference over description as it should be unique, description may not be (E.g. 'Cafe Zetta' for 'CAF001' and 'CAF002').
          columnName = data.reference || data.description;
        }
        if (!columnName) {
          columnName = defaultLabel;
        }
        return {
          id: `PREVIOUS YEAR - ${columnName}`,
          data: previousColumns
            .filter((column) => column.key.includes('period_'))
            .map((previousColumn) => {
              const column = columns.find((column) => column.periodNumber === previousColumn.periodNumber);

              return {
                x: column ? column.shortName : previousColumn.shortName,
                y: parseFloat(data[previousColumn.key]) || 0,
              };
            }),
        };
      })
    );
  }

  const legends: LegendProps[] = graphOnly
    ? []
    : [
        {
          anchor: 'bottom-right',
          direction: 'column',
          justify: false,
          translateX: 100,
          translateY: 0,
          itemsSpacing: 0,
          itemDirection: 'left-to-right',
          itemWidth: 80,
          itemHeight: 20,
          itemOpacity: 0.75,
          symbolSize: 12,
          symbolShape: 'circle',
          symbolBorderColor: 'rgba(0, 0, 0, .5)',
          effects: [
            {
              on: 'hover',
              style: {
                itemBackground: 'rgba(0, 0, 0, .03)',
                itemOpacity: 1,
              },
            },
          ],
        },
      ];

  const customTooltip = ({
    point: {
      serieId,
      serieColor,
      index,
      data: { yFormatted },
    },
  }) => {
    return (
      <div style={{ backgroundColor: 'white', fontSize: '13px', padding: 3 }}>
        <Chip key={index} color={serieColor} /> {`${serieId} - ${yFormatted}`}
      </div>
    );
  };

  if (graphType === 'LINE') {
    return (
      <>
        <div style={{ height: height, width: '100%' }}>
          <ResponsiveLine
            data={reformedData}
            margin={marginConfig}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false, reverse: false }}
            curve={'catmullRom'}
            axisTop={null}
            axisRight={null}
            axisBottom={axisBottomConfig}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: graphOnly ? null : 'Sales',
              legendOffset: -40,
              legendPosition: 'middle',
              tickValues: 3,
            }}
            colors={colors}
            pointSize={15}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabel="y"
            pointLabelYOffset={-12}
            useMesh={true}
            legends={legends}
            tooltip={customTooltip}
          />
        </div>
      </>
    );
  }
  return (
    <div style={{ height: 500, width: '100%' }}>
      <ResponsiveAreaBump
        data={reformedData as any[]}
        margin={{ top: 40, right: 100, bottom: 40, left: 100 }}
        spacing={20}
        colors={colors}
        blendMode="multiply"
        startLabel={(series: BumpSerie<any, any>) => series?.id}
        axisTop={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: '',
          legendPosition: 'middle',
          legendOffset: -36,
        }}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: '',
          legendPosition: 'middle',
          legendOffset: 32,
        }}
      />
    </div>
  );
};
